import React, { useEffect, useState } from 'react'
import { useStyles } from '../TemplateCardTextProperties/TemplateCardTextProperties.styles'
import { SelectChangeEvent, Typography } from '@mui/material'
import { ColorSelectorV2, SelectV2 } from '@labrav/react-components'
import { useFormikContext } from 'formik'
import clsx from 'clsx'
import {
  extractColorV2,
  hexadecimalColor,
} from '../../../../common/utils/productCreationUtil'
import { convertRGBAAlpha } from '../../../../common/utils/convertRGBAAlpha'
import { calculateMinMaxResult } from '../../../AddEditProductFormComponents/FlyOutProductBranding/FlyOutProductBranding'
import { useStyles as commonStyles } from '../../FlyOutStorefront.styles'
import {
  fontSizes,
  fontWeights,
} from '../../../../utils/templateCreationConstants'
import { hexToRgb, rgbToHex } from '../../../../utils/colorsConvertor'
import { ListingCardInCreation } from '../../../../modules/storefront/listingCardCreation/reducer'

export const TemplateCardBodyTextProperties: React.FC = () => {
  const { classes } = useStyles()
  const { classes: commonClasses } = commonStyles()
  const { values, errors, touched, setFieldValue, handleBlur, handleChange } =
    useFormikContext<ListingCardInCreation>()
  const { listingCardData } = values
  const [colorType, setColorType] = useState<string>(
    listingCardData?.template?.data?.cardContent?.customization?.color
      ? listingCardData?.template?.data?.cardContent?.customization?.color?.includes(
          '#'
        )
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )
  const [bodyColor, setBodyColor] = useState<string[]>(() => {
    const colorString =
      listingCardData?.template?.data?.cardContent?.customization?.color || ''
    return extractColorV2(colorType, colorString)
  })
  useEffect(() => {
    const colorString =
      listingCardData?.template?.data?.cardContent?.customization?.color || ''
    setBodyColor(extractColorV2(colorType, colorString))
  }, [listingCardData?.template?.data?.cardContent?.customization?.color])

  const handleTypeChange = async (event: SelectChangeEvent) => {
    const typeValue = event.target.value as string
    const resetValue =
      typeValue === 'RGB'
        ? hexToRgb(bodyColor[4]).toString()
        : rgbToHex(
            parseInt(bodyColor[0]),
            parseInt(bodyColor[1]),
            parseInt(bodyColor[2])
          )
    setColorType(typeValue)
    await setFieldValue(
      'listingCardData.template.data.cardContent.customization.color',
      resetValue
    )
  }

  const handleColorChange = async (index: number, event: SelectChangeEvent) => {
    const updatedColorValues = calculateMinMaxResult(
      index,
      [...bodyColor],
      event.target.value as string
    )
    if (colorType === 'RGB') {
      const color = `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, 100)`
      await setFieldValue(
        'listingCardData.template.data.cardContent.customization.color',
        color
      )
    } else {
      const color = hexadecimalColor(`${updatedColorValues[4]}`, 100)
      await setFieldValue(
        'listingCardData.template.data.cardContent.customization.color',
        color
      )
    }
  }

  return (
    <div
      className={clsx(
        classes.sectionWrapper,
        classes.sectionHeaderWrapper,
        classes.content,
        classes.root
      )}
      data-testid="card-body-text-properties-component"
    >
      <div className={classes.textContentWrapper}>
        <Typography
          className={commonClasses.textHeading}
          data-testid="card-body-text"
        >
          Card body text properties
        </Typography>
      </div>
      <div className={classes.rowContainer}>
        <div className={classes.colorSelectorWidth}>
          <ColorSelectorV2
            data-testid="card-body-text-color-selector"
            selectedField={colorType}
            placeholder={'#FFFFFF'}
            onBlur={handleBlur}
            onChange={handleTypeChange}
            keyName={
              'listingCardData?.template?.data?.cardContent?.customization?.color'
            }
            colorValues={bodyColor}
            handleColorChange={handleColorChange}
            color={convertRGBAAlpha(
              listingCardData?.template?.data?.cardContent?.customization
                .color || '',
              false
            )}
            isError={
              !!(
                (touched as any).listingCardData?.template?.data?.cardContent
                  ?.customization?.color &&
                (errors as any).listingCardData?.template?.data?.cardContent
                  ?.customization?.color
              )
            }
            errorText={
              !!(touched as any).listingCardData?.template?.data?.cardContent
                ?.customization?.color &&
              ((errors as any).listingCardData?.template?.data?.cardContent
                ?.customization?.color ||
                '')
            }
            secondaryVariant={true}
          />
        </div>
        <div>
          <SelectV2
            data-testid="card-body-text-font-size-selector"
            minWidth="119px"
            field={{
              title: 'Font Size',
              placeHolder: 'Font Size',
              id: 'listingCardData?.template?.data?.cardContent?.customization?.fontSize',
              isRequired: false,
              humanReadableKey: 'fontSize',
              isReadOnly: false,
              isCustom: false,
              defaultValue: '12px',
              dataType: 'string',
              options: fontSizes,
            }}
            value={
              listingCardData?.template?.data?.cardContent?.customization
                .fontSize || '12px'
            }
            key={
              'listingCardData.template.data?.cardContent?.customization?.fontSize'
            }
            onChange={handleChange}
            onBlur={handleBlur}
            isError={false}
            errorText={''}
            isRequired={false}
          />
        </div>
        <div>
          <SelectV2
            data-testid="card-body-text-font-weight-selector"
            minWidth="119px"
            field={{
              title: 'Font Weight',
              placeHolder: 'Font Weight',
              id: 'listingCardData?.template?.data?.cardContent?.customization?.fontWeight',
              isRequired: false,
              humanReadableKey: 'fontWeight',
              isReadOnly: false,
              isCustom: false,
              defaultValue: '400',
              dataType: 'string',
              options: fontWeights,
            }}
            value={
              listingCardData?.template?.data?.cardContent?.customization
                ?.fontWeight || '400'
            }
            key={
              'listingCardData.template.data.cardContent.customization.fontWeight'
            }
            onChange={handleChange}
            onBlur={handleBlur}
            isError={false}
            errorText={''}
            isRequired={false}
          />
        </div>
      </div>
    </div>
  )
}
